import {
  mapEvaluationTemplateToTarget,
  mapTargetToEvaluationTemplate,
} from "@/pages/admin/CriteriaPage/components/criteriaGridHelpers";

import $api from "@http";
import Ajv from "ajv";

const EVALUATION_TEMPLATE_STRUCTURE_SCHEMA = {
  type: "array",
  minItems: 1,
  items: {
    type: "object",
    required: ["id", "name", "criteria"],
    properties: {
      id: {
        type: "string",
        minLength: 1,
      },
      name: {
        type: "string",
        minLength: 1,
      },
      criteria: {
        type: "array",
        minItems: 1,
        items: {
          type: "object",
          required: ["id", "desc", "tool", "params", "weight"],
          properties: {
            id: {
              type: "string",
              minLength: 1,
            },
            desc: {
              type: "string",
              minLength: 1,
            },
            tool: {
              type: "string",
              enum: [
                "moment_detector",
                "counter_match",
                "counter",
                "llm_eval",
                "document_based_eval",
              ],
            },
            params: {
              oneOf: [
                {
                  type: "null",
                },
                {
                  type: "string",
                  minLength: 1,
                },
              ],
            },
            weight: {
              type: "integer",
              minimum: 1,
              maximum: 5,
            },
          },
          additionalProperties: false,
        },
      },
    },
    additionalProperties: false,
  },
};

const EVALUATION_TEMPLATE_PAYLOAD_SCHEMA = {
  type: "object",
  required: ["name", "structure"],
  properties: {
    name: {
      type: "string",
      minLength: 1,
    },
    structure: EVALUATION_TEMPLATE_STRUCTURE_SCHEMA,
  },
  additionalProperties: false,
};

const ajv = new Ajv({allErrors: true});
const validateEvaluationTemplatePayload = ajv.compile(
  EVALUATION_TEMPLATE_PAYLOAD_SCHEMA,
);

const buildEvaluationTemplatePayload = (target) =>
  mapTargetToEvaluationTemplate(target);

const assertTargetValid = (target) => {
  const payload = buildEvaluationTemplatePayload(target);

  if (!validateEvaluationTemplatePayload(payload)) {
    const error = new Error("Evaluation template validation failed");
    error.validationErrors = validateEvaluationTemplatePayload.errors ?? [];
    throw error;
  }

  return payload;
};

const mapEvaluationTemplateRow = (item) => ({
  id: item.id,
  name: item.name ?? "",
  created_at: item.created_at,
  updated_at: item.updated_at,
  state: "active",
});

const formatCreatedAtSearch = (value) => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

const applyFilters = (items, {search}) => {
  let result = [...items];

  if (search) {
    const query = String(search).trim();
    const dateRegex = /^(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])-(\d{4})$/;

    if (dateRegex.test(query)) {
      result = result.filter((item) => {
        if (!item.created_at) {
          return false;
        }

        return formatCreatedAtSearch(item.created_at) === query;
      });
    } else {
      const normalizedQuery = query.toLowerCase();

      result = result.filter((item) =>
        item.name?.toLowerCase().includes(normalizedQuery),
      );
    }
  }

  return result;
};

const applySort = (items, sort) => {
  if (!sort?.value) return items;

  const direction = sort.direction === "asc" ? 1 : -1;

  return [...items].sort((a, b) => {
    const left = a[sort.value];
    const right = b[sort.value];

    if (left == null && right == null) return 0;
    if (left == null) return 1;
    if (right == null) return -1;

    if (left < right) return -1 * direction;
    if (left > right) return 1 * direction;
    return 0;
  });
};

const paginate = (items, {page = 1, size = 5}) => {
  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = Math.max(1, Number(size) || 5);
  const offset = (safePage - 1) * safeSize;

  return {
    data: items.slice(offset, offset + safeSize),
    total: items.length,
  };
};

export default class CriteriaService {
  static isTargetValid(target) {
    return validateEvaluationTemplatePayload(
      buildEvaluationTemplatePayload(target),
    );
  }

  static async getCriteria(params = {}) {
    const response = await $api.get("/api/v1/evaluation_templates");
    const items = (response.data ?? []).map(mapEvaluationTemplateRow);

    const filtered = applySort(applyFilters(items, params), params.sort);
    const {data, total} = paginate(filtered, params);

    return {data: {data, total}};
  }

  static async listAllCriteria() {
    const response = await $api.get("/api/v1/evaluation_templates");

    return {
      data: (response.data ?? []).map(mapEvaluationTemplateRow),
    };
  }

  static async viewCriteria({id}) {
    const response = await $api.get(`/api/v1/evaluation_templates/${id}`);

    return {data: mapEvaluationTemplateToTarget(response.data)};
  }

  static async createCriteria({target}) {
    const evaluationTemplate = assertTargetValid(target);
    const response = await $api.post("/api/v1/evaluation_templates", {
      evaluation_template: evaluationTemplate,
    });

    return {data: mapEvaluationTemplateToTarget(response.data)};
  }

  static async updateCriteria({id, target}) {
    const evaluationTemplate = assertTargetValid(target);
    const response = await $api.patch(`/api/v1/evaluation_templates/${id}`, {
      evaluation_template: evaluationTemplate,
    });

    return {data: mapEvaluationTemplateToTarget(response.data)};
  }

  static async deleteCriteria({id}) {
    await $api.delete(`/api/v1/evaluation_templates/${id}`);

    return {data: {id}};
  }
}
